import React, { useRef, useState, useEffect } from 'react';
import { X, Sliders, RotateCw } from 'lucide-react';
import { ACCEPT_ATTRIBUTE } from '@/config/uploadFormats';

interface MobileImageCaptureProps {
  onCapture: (files: File[]) => void;
  onClose: () => void;
}

const MobileImageCapture: React.FC<MobileImageCaptureProps> = ({ onCapture, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [showGallerySheet, setShowGallerySheet] = useState(false);
  const [selectedGalleryImages, setSelectedGalleryImages] = useState<File[]>([]);
  const [dragStartY, setDragStartY] = useState(0);
  const [sheetTranslateY, setSheetTranslateY] = useState(0);

  // Start camera on mount
  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [facingMode]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        setStream(mediaStream);
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const switchCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture([file]);
        onClose();
      }
    }, 'image/jpeg', 0.95);
  };

  const handleGallerySelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedGalleryImages(Array.from(files));
    }
  };

  const handleGalleryDone = () => {
    if (selectedGalleryImages.length > 0) {
      onCapture(selectedGalleryImages);
      onClose();
    }
  };

  const toggleGallerySheet = () => {
    if (!showGallerySheet) {
      fileInputRef.current?.click();
    }
    setShowGallerySheet(!showGallerySheet);
  };

  const handleSheetDragStart = (e: React.TouchEvent) => {
    setDragStartY(e.touches[0].clientY);
  };

  const handleSheetDragMove = (e: React.TouchEvent) => {
    const currentY = e.touches[0].clientY;
    const diff = currentY - dragStartY;
    if (diff > 0) {
      setSheetTranslateY(diff);
    }
  };

  const handleSheetDragEnd = () => {
    if (sheetTranslateY > 100) {
      setShowGallerySheet(false);
    }
    setSheetTranslateY(0);
    setDragStartY(0);
  };

  return (
    <div className="fixed inset-0 bg-black z-[70] flex flex-col">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ATTRIBUTE}
        multiple
        onChange={handleGallerySelect}
        className="hidden"
      />
      <canvas ref={canvasRef} className="hidden" />

      {/* Top Controls */}
      <div className="absolute top-0 left-0 right-0 z-20 p-4 pt-safe flex items-center justify-between">
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center text-white active:scale-90 transition-transform"
        >
          <X size={24} />
        </button>
        <button
          className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center text-white active:scale-90 transition-transform"
        >
          <Sliders size={22} />
        </button>
      </div>

      {/* Camera View */}
      <div className="flex-1 relative">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />

        {/* Face detection frame overlay (decorative) */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-64 h-80">
            {/* Corner brackets */}
            <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-white/60" />
            <div className="absolute top-0 right-0 w-16 h-16 border-r-2 border-t-2 border-white/60" />
            <div className="absolute bottom-0 left-0 w-16 h-16 border-l-2 border-b-2 border-white/60" />
            <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-white/60" />
          </div>
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-0 left-0 right-0 pb-safe">
        <div className="bg-gradient-to-t from-black/80 via-black/60 to-transparent pt-8 pb-6 px-4">
          <div className="flex items-center justify-center gap-8">
            {/* Gallery Button with thumbnail */}
            <button
              onClick={toggleGallerySheet}
              className="relative w-14 h-14 rounded-xl overflow-hidden border-2 border-white/80 active:scale-90 transition-transform"
            >
              <div className="w-full h-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center">
                <div className="grid grid-cols-2 gap-0.5 w-8 h-8">
                  <div className="bg-white/40 rounded-sm" />
                  <div className="bg-white/60 rounded-sm" />
                  <div className="bg-white/50 rounded-sm" />
                  <div className="bg-white/70 rounded-sm" />
                </div>
              </div>
            </button>

            {/* Capture Button */}
            <button
              onClick={capturePhoto}
              className="relative active:scale-90 transition-transform"
            >
              <div className="w-20 h-20 rounded-full border-4 border-white flex items-center justify-center">
                <div className="w-[68px] h-[68px] rounded-full bg-white" />
              </div>
            </button>

            {/* Flip Camera Button */}
            <button
              onClick={switchCamera}
              className="w-14 h-14 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center text-white active:scale-90 transition-transform"
            >
              <RotateCw size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Gallery Bottom Sheet */}
      {showGallerySheet && selectedGalleryImages.length > 0 && (
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm z-30"
          onClick={() => setShowGallerySheet(false)}
        >
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[70vh] overflow-hidden"
            style={{ transform: `translateY(${sheetTranslateY}px)` }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleSheetDragStart}
            onTouchMove={handleSheetDragMove}
            onTouchEnd={handleSheetDragEnd}
          >
            {/* Drag handle */}
            <div className="flex justify-center py-3">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-4 pb-3 flex items-center justify-between border-b">
              <h3 className="text-lg font-semibold">Gallery</h3>
              <button
                onClick={handleGalleryDone}
                className="px-4 py-2 bg-blue-500 text-white rounded-full font-medium text-sm active:scale-95 transition-transform"
              >
                Done ({selectedGalleryImages.length})
              </button>
            </div>

            {/* Gallery Grid */}
            <div className="p-2 grid grid-cols-3 gap-1 overflow-y-auto max-h-[calc(70vh-100px)]">
              {selectedGalleryImages.map((file, index) => (
                <div key={index} className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Selected ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileImageCapture;
